import axios from 'axios';
import { useTestMode } from './composables/useTestMode.js';

// Determine the API base URL dynamically
const getApiBaseUrl = () => {
  // Detect if we're in a Vercel environment (deployed, not local)
  const isVercelDeployment = typeof window !== 'undefined' && window.location && 
    (window.location.hostname.includes('vercel.app') || window.location.hostname !== 'localhost');
  
  // Local development: use configured URL
  if (process.env.NODE_ENV === 'development' && !isVercelDeployment) {
    console.log('[API Service] Local development mode');
    return process.env.VUE_APP_API_URL || 'http://localhost:3000';
  }
  
  // Vercel deployment (production or preview): ALWAYS use same domain
  if (typeof window !== 'undefined' && window.location) {
    const origin = window.location.origin;
    console.log(`[API Service] Using dynamic origin (Vercel): ${origin}`);
    return origin;
  }
  
  // SSR/Build time: Try to detect from environment
  if (process.env.VERCEL_URL) {
    const vercelUrl = `https://${process.env.VERCEL_URL}`;
    console.log(`[API Service] Using VERCEL_URL: ${vercelUrl}`);
    return vercelUrl;
  }
  
  // Final fallback
  console.warn('[API Service] Using fallback URL - this may cause CORS issues');
  return process.env.VUE_APP_API_URL || 'https://tierlist-app.vercel.app';
};

const apiClient = axios.create({
  baseURL: (() => {
    const baseUrl = getApiBaseUrl();
    console.log(`[API Service] Base URL set to: ${baseUrl}`);
    console.log(`[API Service] Current window origin: ${typeof window !== 'undefined' ? window.location?.origin : 'N/A (SSR)'}`);
    console.log(`[API Service] NODE_ENV: ${process.env.NODE_ENV}`);
    return baseUrl;
  })(),
  timeout: 15000, // Increased timeout for serverless cold starts
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': process.env.VUE_APP_API_KEY, // API key from environment
  },
});

// Test mode instance
const { includeTestAlbumsParam } = useTestMode();

// Retry helper for serverless functions
const retryRequest = async (requestFn, maxRetries = 2) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      // Retry on timeout or 5xx errors (serverless cold starts)
      if (attempt < maxRetries && (
        error.code === 'ECONNABORTED' || 
        (error.response && error.response.status >= 500)
      )) {
        console.warn(`Request failed (attempt ${attempt}/${maxRetries}), retrying...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
        continue;
      }
      throw error;
    }
  }
};

// Verify admin password with backend
const verifyPassword = async (password) => {
  try {
    // Use direct route in development mode for better compatibility
    const endpoint = process.env.NODE_ENV === 'development' 
      ? '/api/auth/verify-password-direct' 
      : '/api/auth/verify-password';
    
    const response = await apiClient.post(endpoint, { password });
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || error.response.data?.error || 'Authentication failed';
      throw new Error(message);
    }
    throw new Error('Network error');
  }
};

const fetchAlbums = async (options = {}) => {
  try {
    const {
      sessionId = null,
      page = 1,
      limit = 10,
      search = '',
      searchMode = 'auto',
      tier = null,
      include_test_albums = includeTestAlbumsParam.value,
      ...additionalFilters
    } = options;
    
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      include_test_albums,
      ...additionalFilters
    });
    
    if (sessionId) params.set('session_id', sessionId);
    if (tier) params.set('tier', tier);
    if (search && search.trim()) {
      params.set('search', search.trim());
      params.set('searchMode', searchMode);
    }
    
    const response = await retryRequest(() => apiClient.get(`/api/albums?${params}`));
    
    // Return enhanced response with pagination and search metadata
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || {},
      hasNext: response.data.pagination?.hasNext || false,
      search: response.data.search || { term: '', mode: 'auto', isActive: false, resultCount: 0 }
    };
  } catch (error) {
    console.error('Error fetching albums', error);
    throw error;
  }
};

// Legacy method for tierlist view - fetches ALL albums without pagination
const fetchAllAlbums = async (sessionId = null, additionalFilters = {}) => {
  try {
    const params = new URLSearchParams({
      page: '1',
      limit: '1000', // High limit to get all albums
      include_test_albums: includeTestAlbumsParam.value,
      ...additionalFilters
    });
    
    if (sessionId) {
      params.set('session_id', sessionId);
    }
    
    const response = await retryRequest(() => apiClient.get(`/api/albums?${params}`));
    
    // Return in legacy format for backward compatibility with tierlist
    return {
      success: true,
      data: response.data.data || []
    };
  } catch (error) {
    console.error('Error fetching all albums', error);
    throw error;
  }
};

const getAlbum = async (id) => {
  try {
    const response = await apiClient.get(`/api/albums/${id}`);
    return response.data.data; // Ensure it matches the response structure
  } catch (error) {
    console.error('Error fetching album', error);
    throw error;
  }
};

const updateAlbum = async (id, album, password) => {
  try {
    const response = await apiClient.put(`/api/albums/${id}`, { ...album, password });
    return response.data.data;
  } catch (error) {
    if (error.response) {
      console.error('Error updating album:', error.response.data);
      throw new Error(error.response.data.error || error.response.statusText);
    } else {
      console.error('Error updating album:', error.message);
      throw new Error('Error updating album');
    }
  }
};

const deleteAlbum = async (id, password) => {
  try {
    console.log('Deleting album:', { id, hasPassword: !!password });
    const response = await apiClient.delete(`/api/albums/${id}`, {
      data: { password }
    });
    
    console.log('Delete album response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data
    });
    
    // Handle successful response
    if (response.data && response.data.success) {
      return response.data.data || response.data;
    } else if (response.data && response.data.data) {
      return response.data.data;
    } else {
      return response.data;
    }
  } catch (error) {
    console.error('Delete album error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      stack: error.stack
    });
    
    if (error.response) {
      const errorMessage = error.response.data?.message || 
                          error.response.data?.error || 
                          error.response.statusText || 
                          `HTTP ${error.response.status} error`;
      throw new Error(errorMessage);
    } else {
      throw new Error('Network error or request failed');
    }
  }
};

const getGenres = async () => {
  try {
    const response = await apiClient.get('/api/genres');
    return response.data.data; // { mainGenres: [...], subGenres: [...] }
  } catch (error) {
    console.error('Error fetching genres:', error);
    return { mainGenres: [], subGenres: [] };
  }
};

const updateAlbumGenres = async (albumId, { mainGenres = [], subGenres = [] }, password) => {
  try {
    const response = await apiClient.put(`/api/albums/${albumId}/genres`, { mainGenres, subGenres, password });
    return response.data;
  } catch (error) {
    console.error('Error updating album genres:', error);
    throw error;
  }
};

const backfillGenres = async (password, { dryRun = false, exportCsv = false } = {}) => {
  try {
    const params = {};
    if (dryRun) params.dry_run = 'true';
    if (exportCsv) params.export = 'csv';
    const response = await apiClient.post('/api/admin/backfill-genres', { password }, { params });
    return response.data;
  } catch (error) {
    console.error('Error running genre backfill:', error);
    throw error;
  }
};

const importGenresCsv = async (assignments, password) => {
  try {
    const response = await apiClient.post('/api/admin/import-genres-csv', { assignments, password });
    return response.data;
  } catch (error) {
    console.error('Error importing genres:', error);
    throw error;
  }
};

const fetchChangelogs = async (page = 1, limit = 20, filters = {}) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...filters
    });

    const response = await retryRequest(() => apiClient.get(`/api/changelog?${params}`));

    // Handle both old format (direct data) and new format (with pagination)
    if (response.data.pagination) {
      return {
        data: response.data.data,
        pagination: response.data.pagination,
        hasNext: response.data.pagination.hasNext
      };
    } else {
      // Fallback for old API format during transition
      return {
        data: response.data.data,
        pagination: {
          page: 1,
          limit: response.data.data.length,
          total: response.data.data.length,
          pages: 1,
          hasNext: false,
          hasPrev: false
        },
        hasNext: false
      };
    }
  } catch (error) {
    console.error('Error fetching changelogs', error);
    throw error;
  }
};

const getAlbumChangelog = async (albumId, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      limit: params.limit || 50,
      offset: params.offset || 0
    });

    const response = await retryRequest(() => apiClient.get(`/api/albums/${albumId}/changelog?${queryParams}`));
    return response.data; // Returns { success, data: { album_id, changelog, ... }, pagination }
  } catch (error) {
    console.error('Error fetching album changelog:', error);
    throw error;
  }
};

// New method to fetch Spotify album details by spotify_id
const getSpotifyAlbum = async (spotifyId) => {
  try {
    const response = await apiClient.get(`/api/spotify/albums/${spotifyId}`);
    return response.data; // Adjust as necessary to match the response structure
  } catch (error) {
    console.error('Error fetching Spotify album data:', error);
    throw new Error('Failed to fetch Spotify album data');
  }
};

const calculateScore = async (tiers) => {
  try {
    const response = await apiClient.post('/api/ratings', { tiers }, {
      headers: {
        'x-api-key': process.env.VUE_APP_API_KEY
      }
    });
    return response.data.data; // Extract { averageScore, suggestedTier }
  } catch (error) {
    console.error('Error calculating score:', error);
    throw error;
  }
};

// Fetch all review sessions
const WorkspaceSessions = async () => {
  try {
    const params = new URLSearchParams({
      include_test_albums: includeTestAlbumsParam.value
    });
    
    const response = await apiClient.get(`/api/sessions?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sessions', error);
    throw error;
  }
};

// Fetch sessions with pagination support (for infinite scroll) and search
const fetchSessions = async (page = 1, limit = 5, search = '', searchMode = 'auto') => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      includeAlbums: 'true', // Ensure album data is included for session display
      include_test_albums: includeTestAlbumsParam.value
    });
    
    // Add search parameters if provided
    if (search && search.trim()) {
      params.append('search', search.trim());
      params.append('searchMode', searchMode);
    }
    
    const response = await retryRequest(() => apiClient.get(`/api/sessions?${params}`));
    
    // The backend SessionsService.listSessions already returns the proper format
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 5, total: 0, pages: 0, hasNext: false, hasPrev: false },
      hasNext: response.data.pagination?.hasNext || false,
      search: response.data.search || { term: '', mode: 'auto', isActive: false }
    };
  } catch (error) {
    console.error('Error fetching sessions with pagination', error);
    throw error;
  }
};

// Create a new session
const createSession = async (sessionDataWithPassword) => {
  try {
    const response = await apiClient.post('/api/sessions', sessionDataWithPassword);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error('Error creating session:', error.response.data);
      throw new Error(error.response.data.error || error.response.statusText);
    } else {
      console.error('Error creating session:', error.message);
      throw new Error('Error creating session');
    }
  }
};

// Update an existing session
const updateSession = async (sessionId, sessionDataWithPassword) => {
  try {
    const response = await apiClient.put(`/api/sessions/${sessionId}`, sessionDataWithPassword);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error('Error updating session:', error.response.data);
      throw new Error(error.response.data.error || error.response.statusText);
    } else {
      console.error('Error updating session:', error.message);
      throw new Error('Error updating session');
    }
  }
};

// Upload album cover image to backend (returns S3 URL)
const uploadAlbumImage = async (formData) => {
  try {
    const response = await axios.post(
      getApiBaseUrl() + '/api/upload/album-cover',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-api-key': process.env.VUE_APP_API_KEY,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.error || error.response.statusText);
    } else {
      throw new Error('Image upload failed');
    }
  }
};

const getTimeline = async (sort = 'desc') => {
  const response = await apiClient.get('/api/albums/timeline', { params: { sort } });
  return response;
};

// Create a new album
const createAlbum = async (albumData) => {
  try {
    const response = await apiClient.post('/api/albums', albumData);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.error || error.response.statusText);
    } else {
      throw new Error('Album creation failed');
    }
  }
};

export default {
  fetchAlbums,
  fetchAllAlbums,
  getAlbum,
  updateAlbum,
  deleteAlbum,
  getGenres,
  updateAlbumGenres,
  backfillGenres,
  importGenresCsv,
  fetchChangelogs,
  getAlbumChangelog,
  fetchSessions,
  calculateScore,
  getSpotifyAlbum,
  verifyPassword,
  WorkspaceSessions,
  createSession,
  updateSession,
  uploadAlbumImage,
  createAlbum,
  getTimeline,
};
